import { Container, IDestroyOptions, Rectangle, Sprite, Text } from "pixi.js";
import { IPiggyController } from "../../controller/IPiggyController";
import { PiggyResourcePackage } from "../../resources/PiggyResourcePackage";
import { PiggyInfoComponent } from "./PiggyInfoComponent";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { PiggyViewTextStyle } from "../textstyle/PiggyViewTextStyle";
import { PiggyViewSmallTextStyle } from "../textstyle/PiggyViewSmallTextStyle";
import { StringFormatExtention } from "../../../../../../extensions/StringFormatExtention";
import { IPiggyBankModel } from "../../../../common/models/piggybank/IPiggyBankModel";
import { NoCacheDynamicPackage } from "../../../../common/resources/NoCacheDynamicPackage";

export class PiggyInfoLockedComponent extends PiggyInfoComponent {
    private static readonly TOKEN_DESCRIPTION_TEXT = '+ACCRUED FROM UPGRADES';
    private static readonly EXPIRES_IN_TEXT = 'EXPIRES IN ';

    private readonly piggyDynamicResources: NoCacheDynamicPackage;
    private readonly piggyController: IPiggyController;
    private readonly piggyModel: IPiggyBankModel;
    private readonly isNewBank: boolean;

    private contentContainer: Container;
    private tokenImage: Sprite;
    private tokenCount: Text;
    private tokenDescriptionText: Text;
    private tokenInfoButton: ButtonWithScaleAnimation;

    private couponImage: Sprite;
    private couponValue: Text;
    private couponExpires: Text;
    private couponInfoButton: ButtonWithScaleAnimation;

    public constructor(
        piggyResources: PiggyResourcePackage,
        piggyDynamicResources: NoCacheDynamicPackage,
        piggyController: IPiggyController,
        piggyModel: IPiggyBankModel,
        isNewBank: boolean
    ) {
        super(piggyResources);
        this.piggyDynamicResources = piggyDynamicResources;
        this.piggyController = piggyController;
        this.piggyModel = piggyModel;
        this.isNewBank = isNewBank;
        this.build();
        this.layout();
        this.tokenInfoButton.onUp.add(this.onTokenInfoButtonClickHandle, this);
    }

    private build(): void {
        this.contentContainer = new Container();
        this.addChild(this.contentContainer);

        this.tokenImage = new Sprite(this.piggyResources.assets_views_piggy_token_png);
        this.tokenImage.width = this.tokenImage.height = 200;
        this.tokenCount = new Text(StringFormatExtention.tokensFormat(this.piggyModel.tokensBalance.value), new PiggyViewTextStyle());
        this.tokenCount.skew.x = -0.087;
        this.tokenCount.angle = -5;
        this.tokenInfoButton = new ButtonWithScaleAnimation(this.piggyResources.assets_views_piggy_info_icon_png);
        this.tokenInfoButton.width = this.tokenInfoButton.height = 43;
        this.tokenInfoButton.hitArea = new Rectangle(-this.tokenInfoButton.width * 0.5, -this.tokenInfoButton.height, this.tokenInfoButton.width * 2, this.tokenInfoButton.height * 2);
        this.contentContainer.addChild(
            this.tokenImage,
            this.tokenCount,
            this.tokenInfoButton,
        );

        const piggyBankBenefistAtMinHours = this.piggyModel.piggyBankBenefistAtMinHours;
        if (piggyBankBenefistAtMinHours) {
            this.couponImage = new Sprite(this.piggyDynamicResources.resource(piggyBankBenefistAtMinHours.imageUrl));
            this.couponImage.width = 210;
            this.couponImage.height = 200;
            this.couponValue = new Text(`${piggyBankBenefistAtMinHours.buildingPurchaseCouponDiscountPercentage}%`, new PiggyViewTextStyle());
            this.couponValue.skew.x = -0.087;
            this.couponValue.angle = -5;
            this.couponExpires = new Text(PiggyInfoLockedComponent.EXPIRES_IN_TEXT + StringFormatExtention.expiresFromHoursFormat(piggyBankBenefistAtMinHours.buildingPurchaseCouponDurationInHours), new PiggyViewSmallTextStyle());
            this.couponExpires.skew.x = -0.087;
            this.couponExpires.angle = -5;
            this.couponInfoButton = new ButtonWithScaleAnimation(this.piggyResources.assets_views_piggy_info_icon_png);
            this.couponInfoButton.width = this.couponInfoButton.height = 43;
            this.couponInfoButton.onUp.add(this.onCouponInfoButtonClickHandle, this);
            this.couponInfoButton.hitArea = new Rectangle(-this.couponInfoButton.width * 0.5, -this.couponInfoButton.height, this.couponInfoButton.width * 2, this.couponInfoButton.height * 2);
            this.contentContainer.addChild(
                this.couponImage,
                this.couponValue,
                this.couponExpires,
                this.couponInfoButton
            );
        }
        if (this.isNewBank) {
            this.tokenDescriptionText = new Text(PiggyInfoLockedComponent.TOKEN_DESCRIPTION_TEXT, new PiggyViewSmallTextStyle());
            this.tokenDescriptionText.skew.x = -0.087;
            this.tokenDescriptionText.angle = -5;
            this.contentContainer.addChild(this.tokenDescriptionText);
        }
    }

    private layout(): void {
        this.contentContainer.position.set(46, 176);
        this.tokenImage.position.set(33, 48);
        this.tokenCount.position.set(this.tokenImage.position.x + this.tokenImage.width * 0.5 - this.tokenCount.width * 0.5, 247);
        this.tokenInfoButton.position.set(47, 47);
        this.couponImage?.position.set(300, 14);
        this.couponValue?.position.set(this.couponImage.position.x + this.couponImage.width * 0.5 - this.couponValue.width * 0.5 + 5, 225);
        this.couponExpires?.position.set(this.couponImage.position.x + this.couponImage.width * 0.5 - this.couponExpires.width * 0.5 + 5, 301);
        this.couponInfoButton?.position.set(330, 20);
        if (this.isNewBank) {
            this.tokenDescriptionText.position.set(1, 328);
        }
    }

    private async onTokenInfoButtonClickHandle(): Promise<void> {
        this.tokenInfoButton.eventMode = 'auto';
        await this.piggyController.showTokenInfo();
        this.tokenInfoButton.eventMode = 'static';
    }

    private async onCouponInfoButtonClickHandle(): Promise<void> {
        this.couponInfoButton.eventMode = 'auto';
        await this.piggyController.showCouponInfo();
        this.couponInfoButton.eventMode = 'static';
    }

    public hideRewards(): void {
        this.tokenImage.visible = false;
        if (this.couponImage) {
            this.couponImage.visible = false;
        }
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.tokenInfoButton.onUp.remove(this.onTokenInfoButtonClickHandle, this);
        this.couponInfoButton?.onUp.remove(this.onCouponInfoButtonClickHandle, this);
        super.destroy(options);
    }
}
